import { detectMentions, hasAllMention, parseMarkup, toMarkup, type ConversationView, type LocalMessage } from '@swchat/client-sdk';
import type { Project, TextEntity } from '@swchat/protocol';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type RefObject } from 'react';
import { Icon, IconButton, useMediaQuery } from '../design';
import { formatBytes, quoteText } from '../lib/display';
import { useClient, usePrefs } from '../lib/hooks';
import { composerSuggestions, pickSuggestionText, type Suggestion } from '../lib/mention';
import { LazyEmojiPicker, LazyLocationDialog, LazyPollDialog } from './lazy';
import type { DropHandlers } from './MessageList';

/** Hai lần gửi cùng nội dung trong khoảng này coi là một (Enter bị bộ gõ bắn đôi). */
const DUPLICATE_SUBMIT_MS = 500;

/** Xem trước file đang chờ gửi: ảnh hiện hình thu nhỏ, file khác hiện biểu tượng + tên + dung lượng; bấm × để bỏ. */
function StagedFiles({ files, onRemove }: { files: File[]; onRemove: (index: number) => void }) {
  const urls = useMemo(() => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined)), [files]);
  useEffect(() => () => urls.forEach((u) => u && URL.revokeObjectURL(u)), [urls]);
  return (
    <ul className="ws-staged" data-testid="staged-files" aria-label="File sẽ gửi">
      {files.map((f, i) => (
        <li key={`${i}-${f.name}-${f.size}`} className="ws-staged-item" data-testid="staged-file">
          {urls[i] ? <img src={urls[i]} alt={f.name} /> : <Icon name="clip" size={20} />}
          <span className="ws-staged-name" title={f.name}>
            {f.name}
          </span>
          <span className="ws-muted ws-small">{formatBytes(f.size)}</span>
          <IconButton label={`Bỏ “${f.name}”`} icon="close" size={14} data-testid="staged-remove" onClick={() => onRemove(i)} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Ô soạn tin: gợi ý `@` thành viên (A4) và `#MÃ` dự án (E3), trả lời (A1), sửa tin, đính kèm file (C1), báo đang gõ.
 * Định dạng kiểu Telegram: gõ ký hiệu (`**đậm**`, `_nghiêng_`, `__gạch chân__`, `~~gạch ngang~~`, `||ẩn||`, `mã`,
 * `> trích dẫn`, `[chữ](url)`) hoặc dùng thanh định dạng / phím tắt; ghép emoji, bình chọn, vị trí, nhạc từ nút "+".
 * File dán/đính kèm được giữ lại để xem trước và chỉ gửi khi bấm Gửi (tin chữ gửi trước, rồi tới file).
 * Nội dung ô soạn thuộc component này; gửi/sửa thật do `onSubmit` của hội thoại thực hiện.
 */
export function Composer({
  conversation,
  members,
  projects,
  editing,
  replyTo,
  error,
  upload,
  inputRef,
  stageRef,
  drop,
  onCancelEdit,
  onCancelReply,
  onFiles,
  onSubmit,
}: {
  conversation: ConversationView;
  members: { id: string; name: string }[];
  projects: Project[];
  editing?: LocalMessage;
  replyTo?: LocalMessage;
  error?: string;
  upload?: { name: string; progress: number };
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Cho nơi khác (kéo-thả vào khung chat) thêm file vào danh sách xem trước của ô soạn. */
  stageRef: RefObject<((files: File[]) => void) | null>;
  drop: DropHandlers;
  onCancelEdit: () => void;
  onCancelReply: () => void;
  onFiles: (files: File[]) => void | Promise<void>;
  onSubmit: (value: string, mentions: string[], entities: TextEntity[]) => Promise<void>;
}) {
  const client = useClient();
  const enterToSend = usePrefs().settings.enterToSend;
  const [text, setText] = useState('');
  const [staged, setStaged] = useState<File[]>([]);
  useEffect(() => {
    stageRef.current = (files) => {
      if (!editing) setStaged((fs) => [...fs, ...files]);
    };
    return () => {
      stageRef.current = null;
    };
  }, [stageRef, editing]);
  const [caret, setCaret] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const lastTyping = useRef(0);
  /** A4: id thành viên đã chọn từ danh sách gợi ý — ưu tiên khi nhiều người trùng/giống tên. */
  const picked = useRef(new Set<string>());
  const fileInput = useRef<HTMLInputElement>(null);
  const audioInput = useRef<HTMLInputElement>(null);
  const [formatBar, setFormatBar] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [dialog, setDialog] = useState<'poll' | 'location'>();
  /** Màn hẹp (< 760px): gợi ý ngắn để không bị cắt trong ô soạn một dòng. */
  const compact = useMediaQuery('(max-width: 759px)');
  /** Vị trí con trỏ cần đặt sau khi chèn gợi ý — chỉ áp dụng nếu nội dung vẫn đúng như lúc chèn (người dùng chưa gõ thêm). */
  const pendingCaret = useRef<{ value: string; pos: number }>(undefined);
  const lastSubmit = useRef<{ value: string; at: number }>(undefined);
  useLayoutEffect(() => {
    const p = pendingCaret.current;
    const el = inputRef.current;
    if (!p || !el) return;
    pendingCaret.current = undefined;
    if (el.value !== p.value) return;
    el.focus();
    el.setSelectionRange(p.pos, p.pos);
  }, [text, inputRef]);

  useEffect(() => {
    picked.current.clear();
    setStaged([]);
  }, [conversation.id]);
  // Bấm "Sửa" trên một tin → nạp nội dung tin vào ô soạn.
  useEffect(() => {
    if (editing) setText(toMarkup(editing.text ?? '', editing.entities));
  }, [editing]);

  // A4: gợi ý thành viên (đang hoạt động) khi gõ `@…` ngay trước con trỏ; E3: gợi ý mã dự án khi gõ `#…`.
  const beforeCaret = text.slice(0, caret);
  const composer = composerSuggestions(beforeCaret, conversation.kind === 'direct', members, projects);
  const { suggestions } = composer;

  function pickSuggestion(s: Suggestion) {
    if (s.id) picked.current.add(s.id);
    const [next, pos] = pickSuggestionText(text, caret, s, composer);
    setText(next);
    setCaret(pos);
    setMentionIndex(0);
    // Đặt con trỏ ngay sau lần render có `next` (useLayoutEffect), không dùng requestAnimationFrame: phím gõ tiếp
    // trước khung hình kế sẽ bị con trỏ cũ kéo lùi (test E3 chập chờn).
    pendingCaret.current = { value: next, pos };
  }

  /** Thay đoạn [from, to) bằng `insert`, đặt con trỏ tại `caretAt` (vị trí trong chuỗi mới). */
  function replaceRange(from: number, to: number, insert: string, caretAt: number) {
    const next = text.slice(0, from) + insert + text.slice(to);
    setText(next);
    setCaret(caretAt);
    pendingCaret.current = { value: next, pos: caretAt };
  }

  /** Bọc đoạn đang chọn bằng ký hiệu định dạng (không chọn gì → chèn cặp ký hiệu, con trỏ ở giữa). */
  function wrap(open: string, close = open) {
    const el = inputRef.current;
    const a = el?.selectionStart ?? caret;
    const b = el?.selectionEnd ?? caret;
    replaceRange(a, b, open + text.slice(a, b) + close, a + open.length + (b - a) + (a === b ? 0 : close.length));
  }
  function wrapLink() {
    const el = inputRef.current;
    const a = el?.selectionStart ?? caret;
    const b = el?.selectionEnd ?? caret;
    const label = b > a ? text.slice(a, b) : 'văn bản';
    const insert = `[${label}](https://)`;
    replaceRange(a, b, insert, a + insert.length - 1);
  }
  function quoteLines() {
    const el = inputRef.current;
    const a = el?.selectionStart ?? caret;
    const b = el?.selectionEnd ?? caret;
    const start = text.lastIndexOf('\n', a - 1) + 1;
    const block = text
      .slice(start, b)
      .split('\n')
      .map((l) => (l.startsWith('> ') ? l : `> ${l}`))
      .join('\n');
    replaceRange(start, b, block, start + block.length);
  }
  function insertEmoji(emoji: string) {
    const el = inputRef.current;
    const a = el?.selectionStart ?? caret;
    const b = el?.selectionEnd ?? caret;
    replaceRange(a, b, emoji, a + emoji.length);
    setEmojiOpen(false);
  }

  /** Phím tắt định dạng (Ctrl/⌘ + B, I, U, K, E; Shift+X gạch ngang, Shift+P ẩn). Trả true nếu đã xử lý. */
  function formatShortcut(e: KeyboardEvent): boolean {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;
    const k = e.key.toLowerCase();
    const run = (fn: () => void) => {
      e.preventDefault();
      fn();
      return true;
    };
    if (k === 'b') return run(() => wrap('**'));
    if (k === 'i') return run(() => wrap('_'));
    if (k === 'u') return run(() => wrap('__'));
    if (k === 'e') return run(() => wrap('`'));
    if (k === 'k') return run(wrapLink);
    if (e.shiftKey && k === 'x') return run(() => wrap('~~'));
    if (e.shiftKey && k === 'p') return run(() => wrap('||'));
    return false;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Ký hiệu định dạng → văn bản thuần + đoạn định dạng (client cũ chỉ thấy chữ thuần, không lộ ký hiệu).
    const parsed = parseMarkup(text.trim());
    const value = parsed.text;
    const files = staged;
    const hasText = !!value.trim();
    if (!hasText && !files.length) return;
    // Bộ gõ (IME macOS/Safari) có thể bắn Enter hai lần quanh lúc chốt chữ: cùng nội dung trong vài trăm ms là một lần gửi.
    const last = lastSubmit.current;
    const now = Date.now();
    if (hasText && !files.length && !editing && last && last.value === value && now - last.at < DUPLICATE_SUBMIT_MS) return;
    if (hasText) lastSubmit.current = { value, at: now };
    setText('');
    setStaged([]);
    setEmojiOpen(false);
    // A4: mention tường minh — người đã chọn từ gợi ý xếp trước (thắng khi trùng tên), bỏ người mà `@Tên` không còn
    // trong nội dung; tên gõ tay vẫn được nhận diện (khớp tên dài trước, có ranh giới từ).
    if (hasText) {
      const ordered = [...members.filter((m) => picked.current.has(m.id)), ...members.filter((m) => !picked.current.has(m.id))];
      // `@all` (chỉ trong nhóm/kênh): nhắc mọi thành viên khác mình.
      const mentions = conversation.kind !== 'direct' && hasAllMention(value) ? members.map((m) => m.id) : detectMentions(value, ordered);
      picked.current.clear();
      await onSubmit(value, mentions, parsed.entities);
    }
    if (files.length) await onFiles(files);
  }

  if (conversation.left) {
    return (
      <div className="ws-composer-area">
        <div className="ws-composer-locked" data-testid="composer-locked">
          Bạn đã rời nhóm này — chỉ xem lại được tin trước khi rời trên thiết bị này.
        </div>
      </div>
    );
  }

  return (
    <div className="ws-composer-area">
      <form className="ws-composer" data-testid="composer" onSubmit={submit} {...drop}>
        {editing && (
          <div className="ws-composer-note">
            <Icon name="edit" size={14} />
            <span>Đang sửa tin nhắn</span>
            <button type="button" className="ws-link-btn" onClick={() => (onCancelEdit(), setText(''))}>
              Hủy
            </button>
          </div>
        )}
        {replyTo && (
          <div className="ws-replying" data-testid="composer-reply">
            <div className="ws-quote">
              <strong>{client.getUser(replyTo.sender_user_id)?.display_name ?? '…'}</strong>
              <span>{quoteText(replyTo, () => undefined).text}</span>
            </div>
            <IconButton label="Hủy trả lời" icon="close" size={16} onClick={onCancelReply} />
          </div>
        )}
        {error && (
          <div className="ws-error ws-small" data-testid="chat-error" role="alert">
            {error}
          </div>
        )}
        {upload && (
          <div className="ws-upload" data-testid="upload-progress">
            <span className="ws-small">Đang gửi “{upload.name}”…</span>
            <progress max={1} value={upload.progress} />
          </div>
        )}
        {staged.length > 0 && <StagedFiles files={staged} onRemove={(i) => setStaged((fs) => fs.filter((_, j) => j !== i))} />}
        {suggestions.length > 0 && (
          <ul className="ws-suggest" role="listbox" data-testid="mention-list">
            {suggestions.map((s, i) => (
              <li key={s.key}>
                <button
                  type="button"
                  className={i === mentionIndex % suggestions.length ? 'is-active' : undefined}
                  data-testid="mention-option"
                  onMouseDown={(e) => (e.preventDefault(), pickSuggestion(s))}
                >
                  {s.label}
                  {s.hint && <span className="ws-muted ws-small"> · {s.hint}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {formatBar && (
          <div className="ws-format-bar" role="toolbar" aria-label="Định dạng văn bản" data-testid="format-bar">
            <button type="button" aria-label="Đậm (Ctrl+B)" title="Đậm (Ctrl+B)" data-testid="fmt-bold" onClick={() => wrap('**')}>
              <b>B</b>
            </button>
            <button type="button" aria-label="Nghiêng (Ctrl+I)" title="Nghiêng (Ctrl+I)" data-testid="fmt-italic" onClick={() => wrap('_')}>
              <i>I</i>
            </button>
            <button type="button" aria-label="Gạch chân (Ctrl+U)" title="Gạch chân (Ctrl+U)" data-testid="fmt-underline" onClick={() => wrap('__')}>
              <u>U</u>
            </button>
            <button type="button" aria-label="Gạch ngang (Ctrl+Shift+X)" title="Gạch ngang (Ctrl+Shift+X)" data-testid="fmt-strike" onClick={() => wrap('~~')}>
              <s>S</s>
            </button>
            <IconButton label="Mã (Ctrl+E)" icon="code" size={15} data-testid="fmt-code" onClick={() => wrap('`')} />
            <IconButton label="Chữ ẩn (Ctrl+Shift+P)" icon="eye-off" size={15} data-testid="fmt-spoiler" onClick={() => wrap('||')} />
            <IconButton label="Trích dẫn" icon="quote" size={15} data-testid="fmt-quote" onClick={quoteLines} />
            <IconButton label="Liên kết (Ctrl+K)" icon="link" size={15} data-testid="fmt-link" onClick={wrapLink} />
          </div>
        )}
        {emojiOpen && (
          <div className="ws-composer-pop">
            <LazyEmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
          </div>
        )}
        {plusOpen && (
          <ul className="ws-plus-menu" role="menu" aria-label="Chèn thêm" data-testid="plus-menu">
            <li>
              <button type="button" role="menuitem" data-testid="plus-poll" onClick={() => (setPlusOpen(false), setDialog('poll'))}>
                <Icon name="chart" size={15} /> Bình chọn
              </button>
            </li>
            <li>
              <button type="button" role="menuitem" data-testid="plus-location" onClick={() => (setPlusOpen(false), setDialog('location'))}>
                <Icon name="location" size={15} /> Vị trí
              </button>
            </li>
            <li>
              <button type="button" role="menuitem" data-testid="plus-music" onClick={() => (setPlusOpen(false), audioInput.current?.click())}>
                <Icon name="music" size={15} /> Nhạc
              </button>
            </li>
          </ul>
        )}
        <div className="ws-composer-row">
          <input
            ref={audioInput}
            type="file"
            accept="audio/*"
            multiple
            hidden
            data-testid="audio-input"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              if (files.length) setStaged((fs) => [...fs, ...files]);
            }}
          />
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            data-testid="attach-input"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              if (files.length) setStaged((fs) => [...fs, ...files]);
            }}
          />
          <IconButton
            label="Đính kèm file"
            title="Đính kèm file (mã hóa đầu cuối, tối đa 100 MB)"
            icon="clip"
            data-testid="attach-button"
            disabled={!!editing}
            onClick={() => fileInput.current?.click()}
          />
          <IconButton
            label="Chèn bình chọn, vị trí, nhạc"
            icon="plus"
            data-testid="plus-button"
            aria-haspopup="menu"
            aria-expanded={plusOpen}
            disabled={!!upload || !!editing}
            onClick={() => (setPlusOpen((v) => !v), setEmojiOpen(false))}
          />
          <IconButton
            label="Định dạng văn bản"
            icon="format"
            data-testid="format-toggle"
            active={formatBar}
            aria-pressed={formatBar}
            onClick={() => setFormatBar((v) => !v)}
          />
          <IconButton
            label="Chèn emoji"
            icon="smile"
            data-testid="emoji-button"
            active={emojiOpen}
            aria-expanded={emojiOpen}
            onClick={() => (setEmojiOpen((v) => !v), setPlusOpen(false))}
          />
          <textarea
            ref={inputRef}
            data-testid="composer-input"
            rows={1}
            value={text}
            aria-label="Nội dung tin nhắn"
            placeholder={compact ? 'Nhập tin nhắn…' : 'Nhập tin nhắn… (@ để nhắc tên, #MÃ-DỰ-ÁN để gắn tag)'}
            onChange={(e) => {
              setText(e.target.value);
              setCaret(e.target.selectionStart);
              setMentionIndex(0);
              if (Date.now() - lastTyping.current > 3000) {
                lastTyping.current = Date.now();
                client.sendTyping(conversation.id);
              }
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
            onPaste={(e) => {
              // Dán ảnh chụp màn hình / file đã sao chép → thêm vào danh sách xem trước, bấm Gửi mới gửi. Có kèm chữ (vd. sao chép từ Word) thì dán chữ.
              const files = [...e.clipboardData.files];
              if (!files.length || e.clipboardData.getData('text/plain') || editing) return;
              e.preventDefault();
              setStaged((fs) => [...fs, ...files]);
            }}
            onKeyDown={(e) => {
              // Enter dùng để chốt chữ của bộ gõ (keyCode 229 trên Safari sau compositionend) không phải lệnh gửi.
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (formatShortcut(e)) return;
              if (suggestions.length) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  setMentionIndex((i) => (i + (e.key === 'ArrowDown' ? 1 : suggestions.length - 1)) % suggestions.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  pickSuggestion(suggestions[mentionIndex % suggestions.length]!);
                  return;
                }
              }
              if (e.key === 'Escape' && replyTo) onCancelReply();
              // Tùy chọn cá nhân: Enter gửi (mặc định) hoặc Ctrl/⌘+Enter gửi, Enter xuống dòng.
              if (e.key === 'Enter' && (enterToSend ? !e.shiftKey : e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <button type="submit" className="ws-btn ws-btn-primary ws-send" data-testid="composer-send" disabled={(!text.trim() && !staged.length) || (!!upload && staged.length > 0)}>
            <Icon name={editing ? 'check' : 'send'} size={15} />
            {editing ? 'Lưu' : 'Gửi'}
          </button>
        </div>
        <div className="ws-composer-foot">
          <Icon name="lock" size={12} />
          <span>Mã hóa đầu cuối · {enterToSend ? 'Enter để gửi, Shift+Enter xuống dòng' : 'Ctrl+Enter để gửi, Enter xuống dòng'}</span>
        </div>
      </form>
      {dialog === 'poll' && <LazyPollDialog conversationId={conversation.id} onClose={() => setDialog(undefined)} />}
      {dialog === 'location' && <LazyLocationDialog conversationId={conversation.id} onClose={() => setDialog(undefined)} />}
    </div>
  );
}
